import { motion } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";

export default function Resume() {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-secondary to-primary">
      <div className="section-padding">
        <div className="container-width">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('resume.title')}
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-accent to-accent-light mx-auto mb-6"></div>
            <p className="text-lg text-muted-light max-w-2xl mx-auto mb-6">
              {t('resume.subtitle')}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="/Anjali Barge_MS.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {t('resume.buttons.view')}
              </a>
              <a 
                href="/Anjali Barge_MS.pdf" 
                download="Anjali_Barge_Resume.pdf"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('resume.buttons.download')}
              </a>
            </div>
          </motion.div>

          {/* PDF Viewer */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="card max-w-6xl mx-auto"
          >
            <div className="w-full" style={{ height: "80vh" }}>
              <iframe
                src="/Anjali Barge_MS.pdf"
                className="w-full h-full rounded-lg border-2 border-accent/20"
                title="Anjali Barge Resume"
              />
            </div>
          </motion.div>

          {/* Quick Summary Below Resume */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="card bg-gradient-to-r from-accent/10 to-accent-light/10 max-w-5xl mx-auto mt-8"
          >
            <h3 className="text-2xl font-bold text-accent mb-6 text-center">{t('resume.competencies.title')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center">
                  <span className="text-accent mr-2">🧠</span>
                  {t('resume.competencies.research.title')}
                </h4>
                <ul className="space-y-1 text-sm text-muted-light">
                  {t('resume.competencies.research.items').map((item: string, idx: number) => (
                    <li key={idx}>• {item}</li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center">
                  <span className="text-accent mr-2">⚡</span>
                  {t('resume.competencies.engineering.title')}
                </h4>
                <ul className="space-y-1 text-sm text-muted-light">
                  {t('resume.competencies.engineering.items').map((item: string, idx: number) => (
                    <li key={idx}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center">
                  <span className="text-accent mr-2">🚀</span>
                  {t('resume.competencies.education.title')}
                </h4>
                <ul className="space-y-1 text-sm text-muted-light">
                  {t('resume.competencies.education.items').map((item: string, idx: number) => (
                    <li key={idx}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-center pt-6 border-t border-accent/20">
              <p className="text-muted-light mb-4 font-medium">
                {t('resume.summary.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/contact" className="btn-primary">
                  {t('resume.summary.cta.contact')}
                </a>
                <a href="/projects" className="btn-secondary">
                  {t('resume.summary.cta.projects')}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}